interface NamedRecord {
  name: string;
}

interface PersonRecord {
  user_id?: number | string;
  id?: number | string;
  first_name: string;
  last_name: string;
  role_type_id?: number;
}

interface ProjectRecord {
  user_ids: (number | string)[];
  name: string;
}

export const sortEventParticipants = (
  attendees: PersonRecord[],
  userId: number | string
): PersonRecord[] => {
  const attendeesList: PersonRecord[] = [];
  attendees.forEach((attendee) => {
    if (attendee.user_id === userId || attendee.id === userId) {
      attendeesList.push(attendee);
    }
  });
  attendees
    .filter((a) => !(a.user_id === userId || a.id === userId))
    .sort((a, b) => {
      const f1 = a.first_name.toLowerCase();
      const f2 = b.first_name.toLowerCase();
      if (f1 !== f2) return f1 < f2 ? -1 : 1;
      const l1 = a.last_name.toLowerCase();
      const l2 = b.last_name.toLowerCase();
      if (l1 !== l2) return l1 < l2 ? -1 : 1;
      return 0;
    })
    .forEach((ele) => attendeesList.push(ele));
  return attendeesList;
};

export const sortProjects = (
  projects: ProjectRecord[],
  userId: number | string
): ProjectRecord[] => {
  const projectsList: ProjectRecord[] = [];
  projects.forEach((project) => {
    if (project.user_ids.includes(userId)) projectsList.push(project);
  });
  projects
    .filter((p) => !p.user_ids.includes(userId))
    .sort((a, b) => {
      const n1 = a.name.toLowerCase();
      const n2 = b.name.toLowerCase();
      if (n1 !== n2) return n1 < n2 ? -1 : 1;
      return 0;
    })
    .forEach((ele) => projectsList.push(ele));
  return projectsList;
};

export const sortByName = <T extends NamedRecord>(items: T[]): T[] => {
  return items.sort((a, b) => {
    const n1 = a.name.toLowerCase();
    const n2 = b.name.toLowerCase();
    if (n1 !== n2) return n1 < n2 ? -1 : 1;
    return 0;
  });
};

const isDate = (date: unknown): boolean =>
  !Number.isNaN(Date.parse(String(date)));

export const sortObjArrByDate = <T extends Record<string, unknown>>(
  objArr: T[],
  keyName: keyof T,
  descending: boolean = false
): T[] => {
  if (!objArr.length) return objArr;
  if (!isDate(objArr[0][keyName])) return objArr;
  const multiplier = descending ? -1 : 1;
  return objArr.sort(
    (a, b) =>
      (new Date(String(a[keyName])).getTime() -
        new Date(String(b[keyName])).getTime()) *
      multiplier
  );
};

/**
 * Sorts an array of `{first_name, last_name}` records alphabetically by
 * first letter of first name, then first letter of last name.
 * Renamed from `sortWithFirstlastName` (in old src/functions.jsx).
 */
export const sortByFirstThenLastName = (
  arr: PersonRecord[]
): PersonRecord[] => {
  return arr.sort((a, b) => {
    const af = a.first_name.charAt(0).toLowerCase();
    const bf = b.first_name.charAt(0).toLowerCase();
    if (af > bf) return 1;
    if (af < bf) return -1;
    const al = a.last_name.charAt(0).toLowerCase();
    const bl = b.last_name.charAt(0).toLowerCase();
    if (al > bl) return 1;
    if (al < bl) return -1;
    return 0;
  });
};
